import { makeAutoObservable, runInAction, reaction } from "mobx";
import { agent } from "../api/agent";
import { ResetPasswordData, User, UserFormValues } from "../models/User";
import { store } from "./store";
import { UpdateLocationDto } from "../models/UpdateLocationDto";
import { socialAuthService } from "../services/socialAuthService";
import { toast } from "react-toastify";
import { LanguageDTO } from "../models/LanguageDTO";
import i18n from "../../i18n";

export default class UserStore {

    user: User | null = null;
    navigate: (path: string, options?: { state?: any }) => void = () => {};
    pendingRedirectUrl: string | null = null;

    constructor() {
        makeAutoObservable(this);

        reaction(
            () => this.user,
            user => {
                if (user) {
                    window.localStorage.setItem('user', JSON.stringify(user));
                } else {
                    window.localStorage.removeItem('user');
                }
            }
        )
    }

    get isLoggedIn() {
        return !!this.user;
    }

    setNavigateFunction = (navigate: (path: string, options?: { state?: any }) => void) => {
        this.navigate = navigate;
    };

    setPendingRedirectUrl = (url: string) => {
        sessionStorage.setItem('pendingRedirectUrl', url);
        runInAction(() => {
            this.pendingRedirectUrl = url;
        });
    };

    clearPendingRedirectUrl = () => {
        sessionStorage.removeItem('pendingRedirectUrl');
        runInAction(() => {
            this.pendingRedirectUrl = null;
        });
    };

    getRedirectUrl = (): string => {
        const memoryUrl = this.pendingRedirectUrl;
        const sessionUrl = sessionStorage.getItem('pendingRedirectUrl');
        return memoryUrl || sessionUrl || '/playground';
    };

    getAndClearRedirectUrl = (): string => {
        const redirectUrl = this.getRedirectUrl();
        this.clearPendingRedirectUrl();
        return redirectUrl;
    };

    login = async (creds: UserFormValues) => {
        try {
            const user = await agent.Account.login(creds);
            store.commonStore.setToken(user.token);
            runInAction(() => this.user = user);
            const redirectUrl = this.getAndClearRedirectUrl();
            if (typeof this.navigate === 'function') {
                this.navigate(redirectUrl);
            } else {
                toast.error((i18n as any).t('user.navigateNotAvailable'));
            }
            store.modalStore.closeModal();
        } catch (error) {
            toast.error((i18n as any).t('user.loginError') + error);
            throw error;
        }
    };

    socialLogin = async (provider: 'google' | 'facebook') => {
        try {
            let user: User;
            if (provider === 'google') {
                user = await socialAuthService.signInWithGoogle();
            } else {
                user = await socialAuthService.signInWithFacebook();
            }
            store.commonStore.setToken(user.token);
            runInAction(() => this.user = user);
            const redirectUrl = this.getAndClearRedirectUrl();
            this.navigate(redirectUrl);
            store.modalStore.closeModal();
        } catch (error: any) {
            const errorKey = provider === 'google' ? 'user.googleLoginError' : 'user.facebookLoginError';
            toast.error((i18n as any).t(errorKey), error);
            throw error;
        }
    };

    logout = async () => {
        if (this.user?.SocialProvider) {
            await socialAuthService.signOut();
        }
        store.commonStore.setToken(null);
        window.localStorage.removeItem('jwt');
        window.localStorage.removeItem('user');
        store.gameStore.reset();
        runInAction(() => {
            this.user = null;
            store.messageStore.clearAll();
        });
        this.navigate('/');
    };

    setLanguage = async (lang: string) => {
        try {
            const dto: LanguageDTO = { language: lang };
            await agent.Account.setLanguage(dto);
            toast.success((i18n as any).t('user.languageUpdated'));
        } catch (error) {
            toast.error((i18n as any).t('user.languageUpdateError'));
        }
    };

    getUser = async () => {
        try {
            const token = window.localStorage.getItem('jwt');
            if (!token) return;

            if (this.isTokenExpired(token)) {
                await this.logout();
                return;
            }

            const user = await agent.Account.current();
            runInAction(() => this.user = user);

        } catch (error: unknown) {
            if (this.isAxiosError(error) && error.response?.status === 401) {
                store.commonStore.setToken(null);
                window.localStorage.removeItem('jwt');
                window.localStorage.removeItem('user');
                runInAction(() => { this.user = null; });
            }
            throw error;
        }
    };

    private isAxiosError(error: unknown): error is { response: { status: number } } {
        return (
            typeof error === 'object' &&
            error !== null &&
            'response' in error &&
            typeof (error as any).response === 'object' &&
            (error as any).response !== null &&
            'status' in (error as any).response &&
            typeof (error as any).response.status === 'number'
        );
    }

    isTokenExpired = (token: string): boolean => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp <= currentTime;
        } catch (error) {
            return true;
        }
    };

    initializeUser = () => {
        try {
            const token = window.localStorage.getItem('jwt');
            const userData = window.localStorage.getItem('user');

            if (token && userData) {
                if (this.isTokenExpired(token)) {
                    store.commonStore.setToken(null);
                    window.localStorage.removeItem('jwt');
                    window.localStorage.removeItem('user');
                    return;
                }
                const user = JSON.parse(userData);
                runInAction(() => {
                    this.user = user;
                });
                store.commonStore.setToken(token);
            }
        } catch (error) {
            store.commonStore.setToken(null);
            window.localStorage.removeItem('jwt');
            window.localStorage.removeItem('user');
        }
    };

    register = async (creds: UserFormValues) => {
        try {
            const user = await agent.Account.register(creds);
            store.commonStore.setToken(user.token);
            runInAction(() => this.user = user);
            const redirectUrl = this.getAndClearRedirectUrl();
            this.navigate(redirectUrl);
            store.modalStore.closeModal();
        } catch (error) {
            throw error;
        }
    };

    forgotPassword = async (email: string) => {
        try {
            await agent.Account.forgotPassword({ email });
            return { success: true, message: (i18n as any).t('user.passwordResetSent') };
        } catch (error: any) {
            throw error;
        }
    };

    resetPassword = async (resetData: ResetPasswordData) => {
        try {
            await agent.Account.resetPassword(resetData);
            return { success: true, message: (i18n as any).t('user.passwordResetSuccess') };
        } catch (error: any) {
            toast.error((i18n as any).t('user.resetPasswordError') + error);
            throw error;
        }
    };

    setImage = (image: string) => {
        if (this.user) {
            runInAction(() => {
                this.user!.image = image;
            });
        }
    };

    setlocation = (updateLocationDto: UpdateLocationDto) => {
        if (this.user) {
            runInAction(() => {
                this.user!.latitude = updateLocationDto.latitude;
                this.user!.longitude = updateLocationDto.longitude;
            });
        }
    };
}